<!-- Settings — the place, the threshold, and whether warnings interrupt you.

     THE NOTIFICATION PROMPT LIVES HERE AND NOWHERE ELSE. It fires on the
     warning-alerts switch, at the moment someone asks for warning alerts — not
     bundled with the location prompt at launch, and not at launch at all. The
     OS gives exactly one prompt; spending it before the person knows what the
     notifications are for is how an app ends up unable to send any. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="settings">
      <div class="sheet">
        <header class="head">
          <h1>Settings</h1>
          <p class="meta">Overhead forecasts for one place.</p>
        </header>

        <section class="block">
          <h2>Place</h2>
          <template v-if="place">
            <p class="value">{{ place.name }}</p>
            <p class="meta">
              {{ place.postcode }}
              <template v-if="place.detectedAt"> · detected {{ stamp(place.detectedAt) }}</template>
            </p>
          </template>
          <p v-else class="meta" :class="{ warn: permissionState === 'denied' }">
            {{ permissionState === 'denied'
              ? 'Location is turned off for Overhead. Turn it on in your device settings and the trace comes back.'
              : 'Location is off. Turn it on to get a forecast for where you are.' }}
          </p>
          <button class="act" type="button" :disabled="busy" @click="locate">
            {{ busy ? 'Locating…' : place ? 'Update my location' : 'Use my location' }}
          </button>
        </section>

        <section class="block">
          <h2>Rain threshold</h2>
          <p class="meta">
            Hours below this do not count as rain — it sets the summary, the mm
            figure on the trace, and when a warning alert is worth sending.
          </p>
          <div class="choices" role="group" aria-label="Rain threshold in millimetres">
            <button
              v-for="mm in THRESHOLDS"
              :key="mm"
              class="choice"
              type="button"
              :class="{ on: Math.abs(thresholdMm - mm) < 0.001 }"
              :aria-pressed="Math.abs(thresholdMm - mm) < 0.001"
              @click="setThreshold(mm)"
            >
              {{ mm.toFixed(1) }} mm
            </button>
          </div>
        </section>

        <section class="block">
          <h2>Warning alerts</h2>
          <p class="meta">
            The strip above the conditions always appears. This is whether a
            severe warning also interrupts you when the app is closed.
          </p>
          <button
            class="act"
            type="button"
            :aria-pressed="warningsEnabled"
            :disabled="pushBusy"
            @click="toggleWarnings"
          >
            {{ warningsEnabled ? 'On' : 'Off' }}
          </button>
          <p v-if="pushNote" class="meta" :class="{ warn: pushWarn }">{{ pushNote }}</p>
        </section>

        <p class="source">
          Sample data. No forecast provider and no warnings feed are connected,
          and nothing is sending notifications yet — the switch above registers
          this device and stops there.
        </p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import '../chrome.js';
import {
  enablePush, disablePush, pushAvailable, deviceToken,
} from '../../addons/push/index.js';
import {
  place, permissionState, thresholdMm, warningsEnabled,
  useMyLocation, setThreshold, setWarningsEnabled, setPushToken,
} from '../weather.js';

const THRESHOLDS = [0.1, 0.2, 0.5, 1];

const busy = ref(false);
const pushBusy = ref(false);
const pushOutcome = ref(null);

async function locate() {
  busy.value = true;
  try { await useMyLocation(); } finally { busy.value = false; }
}

// Notification permission is separate from location permission and is asked
// separately, right here, on the press.
async function toggleWarnings() {
  if (warningsEnabled.value) {
    setWarningsEnabled(false);
    pushOutcome.value = null;
    if (pushAvailable()) await disablePush();
    setPushToken('');
    return;
  }

  pushBusy.value = true;
  try {
    // Web has no APNs or FCM token, and that is not a failure: the in-app strip
    // works everywhere, and push is the extra. Turning the preference on is
    // still the right outcome.
    const outcome = pushAvailable() ? await enablePush() : 'unavailable';
    pushOutcome.value = outcome;
    if (outcome === 'denied') {
      setWarningsEnabled(false);
      return;
    }
    setWarningsEnabled(true);
    if (outcome === 'granted') setPushToken(deviceToken());
  } finally {
    pushBusy.value = false;
  }
}

const pushWarn = computed(() => pushOutcome.value === 'denied');

const pushNote = computed(() => {
  if (pushOutcome.value === 'denied') {
    return 'Notifications are turned off for Overhead. Turn them on in your '
      + 'device settings; the warning strip in the app still works.';
  }
  if (pushOutcome.value === 'unavailable') {
    return 'This build cannot register for push. The warning strip in the app '
      + 'still works.';
  }
  if (pushOutcome.value === 'granted') return 'This device is registered for warning alerts.';
  return '';
});

const stamp = (iso) => (iso
  ? new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  : '');
</script>

<style scoped>
.settings { --background: var(--oh-paper, #dfe3d1); }
.sheet {
  --paper: var(--oh-paper, #dfe3d1);
  --grid: var(--oh-grid, #b0522f);
  --ink: var(--oh-ink, #1b2540);
  --signal: var(--oh-signal, #a8331a);
  background: var(--paper);
  min-height: 100%;
}
.head { padding: env(safe-area-inset-top, 0px) 14px 0; }
.head h1 {
  margin: 16px 0 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-weight: 400;
  font-size: 27px;
  line-height: 1.1;
  color: var(--ink);
}
.block {
  padding: 18px 14px;
  border-top: 1px solid color-mix(in srgb, var(--grid) 22%, transparent);
}
.block:first-of-type { margin-top: 18px; }
h2 {
  margin: 0 0 6px;
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink);
}
.value {
  margin: 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-size: 22px;
  color: var(--ink);
}
.meta {
  margin: 6px 0 0;
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.meta.warn { color: var(--signal); }
.choices { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.act,
.choice {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid color-mix(in srgb, var(--ink) 35%, transparent);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
}
.act { margin-top: 12px; }
.act:focus-visible,
.choice:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.act[disabled] { opacity: 0.5; cursor: default; }
.choice.on { background: var(--ink); color: var(--paper); border-color: var(--ink); }
.source {
  margin: 0;
  padding: 18px 14px calc(20px + env(safe-area-inset-bottom, 0px));
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 10px;
  line-height: 1.6;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--ink) 45%, transparent);
}
</style>
